import type { DeliveryAddress } from '../types/delivery';

type ProfileAddressInput =
  | string
  | {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      zipcode?: string;
      country?: string;
      instructions?: string;
      phone?: string;
    }
  | null
  | undefined;

export function isCompleteDeliveryAddress(
  addr: Partial<DeliveryAddress> | null | undefined
): addr is DeliveryAddress {
  if (!addr) return false;
  return !!(
    addr.street?.trim() &&
    addr.city?.trim() &&
    addr.state?.trim() &&
    addr.zip?.trim()
  );
}

/** Map BookAPT user profile address → DoorDash delivery address fields. */
export function deliveryAddressFromProfile(input: {
  userAddress?: ProfileAddressInput;
  fallbackPhone?: string;
}): DeliveryAddress | null {
  const { userAddress, fallbackPhone } = input;
  if (!userAddress) return null;

  if (typeof userAddress === 'string') {
    const trimmed = userAddress.trim();
    if (!trimmed) return null;
    return {
      street: trimmed,
      city: '',
      state: '',
      zip: '',
      country: 'US',
      phone: fallbackPhone?.trim() || undefined,
    };
  }

  const zip = (userAddress.zip || userAddress.zipcode || '').trim();
  const addr: DeliveryAddress = {
    street: (userAddress.street || '').trim(),
    city: (userAddress.city || '').trim(),
    state: (userAddress.state || '').trim(),
    zip,
    country: userAddress.country?.trim() || 'US',
    instructions: userAddress.instructions?.trim() || '',
    phone: (userAddress.phone || fallbackPhone || '').trim() || undefined,
  };

  return isCompleteDeliveryAddress(addr) ? addr : null;
}

export function profileAddressObjectFromDelivery(
  addr: DeliveryAddress
): Record<string, string> {
  return {
    street: addr.street.trim(),
    city: addr.city.trim(),
    state: addr.state.trim(),
    zipcode: addr.zip.trim(),
    country: addr.country?.trim() || 'US',
    ...(addr.instructions?.trim() ? { instructions: addr.instructions.trim() } : {}),
    ...(addr.phone?.trim() ? { phone: addr.phone.trim() } : {}),
  };
}
